<template>
  <PageLayout
    :reference-id="paymentInvoice.reference_id"
    :amount="formattedAmount"
    :currency="paymentInvoice.currency"
    :default-locale="defaultLocale">
    <template #default="{ colorScheme }">
      <div class="blik-invoice-page__card blik-invoice-page__card--waiting">
        <div
          class="blik-invoice-page__status-icon"
          :class="`blik-invoice-page__status-icon--${stateType}`">
          <svg
            v-if="stateType === 'success'"
            width="60"
            height="60"
            viewBox="0 0 60 60"
            fill="none"
            aria-hidden="true">
            <circle cx="30" cy="30" r="30" fill="currentColor" fill-opacity="0.16" />
            <path
              d="M17.5 31.2L26.1 40.2L43.5 20.4"
              stroke="currentColor"
              stroke-width="3.6"
              stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>
          <div v-else-if="stateType === 'processing'" class="blik-invoice-page__status-spinner" />
          <img
            v-else
            :src="colorScheme === 'dark' ? declinedIconDark : declinedIconLight"
            width="60"
            height="60"
            alt="" />
        </div>

        <h2 class="blik-invoice-page__title">{{ $t(config.titleKey) }}</h2>
        <p class="blik-invoice-page__subtitle">{{ $t(config.textKey) }}</p>

        <div v-if="config.hintKey" class="blik-invoice-page__status-hint">
          {{ $t(config.hintKey) }}
        </div>

        <button
          v-if="hasReturnUrl"
          type="button"
          class="blik-invoice-page__status-back"
          @click="$emit('redirect-to-merchant')">
          {{ $t('blik_invoice__status_back') }}
        </button>
      </div>
    </template>
  </PageLayout>
</template>

<script>
import { mapState } from 'vuex';
import PageLayout from '../components/PageLayout.vue';
import StatusDeclinedDark from '../assets/status-declined-dark.svg';
import StatusDeclinedLight from '../assets/status-declined-light.svg';
import formatInvoiceAmount from '../formatAmount';

const SUCCESS_STATUSES = new Set([
  'processed',
  'authorized',
  'partially_processed',
  'refunded',
  'revoked',
]);

const FAILED_STATUSES = new Set([
  'process_failed',
  'invoke_failed',
  'terminated',
  'expired',
  'voided',
  'charged_back',
]);

const STATE_CONFIG = {
  success: {
    titleKey: 'blik_invoice__status_success_title',
    textKey: 'blik_invoice__status_success_text',
  },
  failed: {
    titleKey: 'blik_invoice__status_declined_title',
    textKey: 'blik_invoice__status_declined_text',
    hintKey: 'blik_invoice__status_declined_hint',
  },
  expired: {
    titleKey: 'blik_invoice__status_expired_title',
    textKey: 'blik_invoice__status_expired_text',
  },
  processing: {
    titleKey: 'blik_invoice__waiting_title',
    textKey: 'blik_invoice__waiting_text',
  },
};

export default {
  name: 'BlikInvoiceStatusPage',
  components: { PageLayout },

  data() {
    return {
      declinedIconDark: StatusDeclinedDark,
      declinedIconLight: StatusDeclinedLight,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData']),

    paymentInvoice() {
      return this.paymentInvoiceData?.data || this.paymentInvoiceData || {};
    },

    hasReturnUrl() {
      return !!this.paymentInvoice.has_return_url;
    },

    currentStatus() {
      return this.paymentInvoice.status || '';
    },

    stateType() {
      if (this.currentStatus === 'expired') {
        return 'expired';
      }

      if (SUCCESS_STATUSES.has(this.currentStatus)) {
        return 'success';
      }

      if (FAILED_STATUSES.has(this.currentStatus)) {
        return 'failed';
      }

      return 'processing';
    },

    config() {
      return STATE_CONFIG[this.stateType] || STATE_CONFIG.processing;
    },

    formattedAmount() {
      return formatInvoiceAmount(this.paymentInvoice.amount, this.paymentInvoice.currency);
    },

    defaultLocale() {
      return this.paymentInvoice?.active_payment?.payload?.default_locale;
    },
  },
};
</script>

<style lang="scss">
@import '../styles';

.blik-invoice-page {
  &__status-icon {
    width: 60px;
    height: 60px;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    &--success {
      color: #2f9e5f;
    }

    img {
      display: block;
      width: 60px;
      height: 60px;
    }
  }

  &__status-spinner {
    width: 54px;
    height: 54px;
    border: 5px solid #f0c5d1;
    border-top-color: #e52222;
    border-radius: 50%;
    animation: blik-invoice-waiting-spin 0.8s linear infinite;
  }

  &__status-hint {
    margin: 0 0 20px;
    padding: 12px 16px;
    border-radius: 12px;
    background: #fff1f2;
    border: 1px solid rgba(231, 79, 88, 0.35);
    color: #9e2634;
    font-size: 13px;
    line-height: 18.85px;
  }

  &__status-back {
    width: 100%;
    height: 50px;
    border: 1px solid #d9d9de;
    border-radius: 10px;
    background: transparent;
    color: #161616;
    font-family: 'Segoe UI', Inter, Arial, sans-serif;
    font-size: 15px;
    line-height: 18px;
    font-weight: 700;
    cursor: pointer;
  }
}

@keyframes blik-invoice-waiting-spin {
  to {
    transform: rotate(360deg);
  }
}

html[data-color-scheme='dark'] {
  .blik-invoice-page {
    &__status-back {
      color: #ffffff;
      border-color: #8a8a8e;
    }

    &__status-hint {
      background: rgba(231, 79, 88, 0.12);
      border: 1px solid rgba(231, 79, 88, 0.35);
      color: #ffb6bb;
    }

    &__status-spinner {
      border-color: #6a4a54;
      border-top-color: #e74f58;
    }
  }
}
</style>
